import { Close } from "@mui/icons-material";
import {
  FormControl,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { FormEvent, useCallback, useState } from "react";
import { useCreatePatientWeightMutation } from "src/api/services/weightTrackerApi";
import { PatientWeight } from "src/api/types";

import LoadingButton from "src/components/Buttons/LoadingButton";
import { isError } from "src/utils/isError";
import { desktopSx } from "src/utils/styles/modalStyles";

interface Props {
  open: boolean;
  onClose: () => void;
  vitals: PatientWeight;
}

const AddNewWeightModal = ({ onClose, open, vitals }: Props) => {
  const [weight, setWeight] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [addWeight, { isLoading }] = useCreatePatientWeightMutation();

  const onSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      setError("");

      if (weight == null) {
        setError("Please enter the patient's new weight");
        return;
      }

      await addWeight({
        weight: weight,
        date: new Date().toLocaleDateString("en-US"),
        bmi: vitals.bmi,
        height: vitals.height,
        goal: vitals.goal ?? null,
        patient_id: vitals.patient_id,
      })
        .unwrap()
        .catch((err) => {
          if (isError(err)) {
            setError(err.data.message);
          }
        })
        .then(() => {
          onClose();
        });
    },
    [
      addWeight,
      onClose,
      vitals.bmi,
      vitals.goal,
      vitals.height,
      vitals.patient_id,
      weight,
    ]
  );

  return (
    <Modal open={open}>
      <Stack sx={desktopSx} gap={4} component="form" onSubmit={onSubmit}>
        <Stack direction="row" justifyContent="space-between">
          <Typography fontWeight={700} variant="h5">
            Add new weight:
          </Typography>
          <IconButton onClick={onClose}>
            <Close fontSize="medium" />
          </IconButton>
        </Stack>
        <Stack gap={2}>
          <FormControl>
            <TextField
              type="number"
              required
              label="Weight (lb)"
              value={weight}
              onChange={(e) => setWeight(Number(e.target.value))}
            />
          </FormControl>
        </Stack>

        <Stack gap={1}>
          {error ? (
            <Typography sx={{ color: "red" }} textAlign="center">
              {error}
            </Typography>
          ) : null}
          <LoadingButton loading={isLoading} type="submit">
            Add
          </LoadingButton>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default AddNewWeightModal;
